import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Database, Plus, Pencil, Trash2, Search, History, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const peso = (n) => `₱${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return '—'; } };
const emptyForm = { material_category: '', item_name: '', unit_type: '', unit_cost: 0 };

export default function MaterialDatabaseTab() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [dialog, setDialog] = useState(null); // { mode:'new'|'edit', id, form }
  const [historyMat, setHistoryMat] = useState(null); // material for history view

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        axios.get(`${API_URL}/api/supply-chain/materials`),
        axios.get(`${API_URL}/api/supply-chain/material-categories`),
      ]);
      setMaterials(m.data || []);
      setCategories(c.data || []);
    } catch { toast.error('Failed to load materials'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return materials.filter(m =>
      (!q || m.item_name.toLowerCase().includes(q) || (m.material_category || '').toLowerCase().includes(q))
    );
  }, [materials, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(m => { (map[m.material_category] = map[m.material_category] || []).push(m); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }));
  }, [filtered]);

  useEffect(() => {
    if (grouped.length === 0) { if (activeCat) setActiveCat(''); return; }
    if (!grouped.some(([c]) => c === activeCat)) setActiveCat(grouped[0][0]);
  }, [grouped, activeCat]);

  const openNew = () => setDialog({ mode: 'new', form: { ...emptyForm } });
  const openEdit = (m) => setDialog({ mode: 'edit', id: m.id, form: { material_category: m.material_category, item_name: m.item_name, unit_type: m.unit_type || '', unit_cost: m.unit_cost || 0 } });

  const save = async () => {
    const f = dialog.form;
    if (!f.material_category.trim() || !f.item_name.trim()) { toast.error('Category and item name are required'); return; }
    const payload = { material_category: f.material_category, item_name: f.item_name, unit_type: f.unit_type, unit_cost: Number(f.unit_cost) || 0 };
    if (dialog.mode === 'new') {
      const dup = materials.some(m =>
        (m.material_category || '').trim().toLowerCase() === f.material_category.trim().toLowerCase() &&
        (m.item_name || '').trim().toLowerCase() === f.item_name.trim().toLowerCase());
      if (dup && !window.confirm(`"${f.item_name.trim()}" already exists in the "${f.material_category.trim()}" category. Add it anyway?`)) return;
    }
    try {
      if (dialog.mode === 'new') await axios.post(`${API_URL}/api/supply-chain/materials`, payload);
      else await axios.put(`${API_URL}/api/supply-chain/materials/${dialog.id}`, payload);
      toast.success(dialog.mode === 'new' ? 'Material added' : 'Material updated');
      setDialog(null);
      load();
    } catch { toast.error('Failed to save material'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try { await axios.delete(`${API_URL}/api/supply-chain/materials/${id}`); toast.success('Material deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const setForm = (patch) => setDialog(d => ({ ...d, form: { ...d.form, ...patch } }));

  return (
    <Card data-testid="material-database-tab">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5 text-emerald-500" /> Material Database
          </CardTitle>
          <Button size="sm" onClick={openNew} data-testid="material-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Material
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} autoComplete="off" onChange={e => setSearch(e.target.value)} placeholder="Search materials or categories..." className="pl-8 h-9 text-sm" data-testid="material-search" />
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
        ) : grouped.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center border rounded-md">No materials yet. Add one, or push items from a Project BOM.</p>
        ) : (
          <Tabs value={activeCat} onValueChange={setActiveCat} className="w-full">
            <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-muted/60 p-1" data-testid="material-category-tabs">
              {grouped.map(([cat, list]) => {
                const missing = list.filter(m => !(Number(m.unit_cost) > 0)).length;
                return (
                  <TabsTrigger key={cat} value={cat} className="text-xs data-[state=active]:bg-background" data-testid={`material-cat-tab-${cat}`}>
                    {cat} <span className="ml-1 text-muted-foreground">({list.length})</span>
                    {missing > 0 && (
                      <span className="ml-1 font-bold text-red-600" title={`${missing} material(s) missing a unit cost`} data-testid={`material-cat-missing-${cat}`}>({missing})</span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
            {grouped.map(([cat, list]) => (
              <TabsContent key={cat} value={cat} className="mt-4">
                <div className="border rounded-md overflow-hidden" data-testid={`material-cat-group-${cat}`}>
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left">Item name</th>
                        <th className="p-2 text-left w-24">Unit</th>
                        <th className="p-2 text-right w-32">Unit Cost</th>
                        <th className="p-2 text-left w-32">Last Updated</th>
                        <th className="p-2 text-center w-28">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(m => {
                        const noCost = !(Number(m.unit_cost) > 0);
                        return (
                          <tr key={m.id} className="border-t hover:bg-muted/40" data-testid={`material-row-${m.id}`}>
                            <td className="p-2">{m.item_name}</td>
                            <td className="p-2">{m.unit_type || '—'}</td>
                            <td className={`p-2 text-right ${noCost ? 'text-red-600 font-semibold' : ''}`}>{peso(m.unit_cost)}</td>
                            <td className="p-2 text-muted-foreground text-xs" data-testid={`material-updated-${m.id}`}>{fmtDate(m.updated_at)}</td>
                            <td className="p-2 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => setHistoryMat(m)} className="text-muted-foreground hover:text-primary" title="Price history" data-testid={`material-history-${m.id}`}><History className="w-3.5 h-3.5" /></button>
                                <button onClick={() => openEdit(m)} className="text-muted-foreground hover:text-primary" data-testid={`material-edit-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                <button onClick={() => remove(m.id)} className="text-red-400 hover:text-red-600" data-testid={`material-delete-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="material-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === 'new' ? 'Add Material' : 'Edit Material'}</DialogTitle></DialogHeader>
          {dialog && (
            <form className="space-y-3" autoComplete="off" onSubmit={e => e.preventDefault()}>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Material Category</Label>
                <Select value={dialog.form._newCat ? '__new__' : (dialog.form.material_category || '')}
                  onValueChange={v => v === '__new__'
                    ? setForm({ _newCat: true, material_category: '' })
                    : setForm({ _newCat: false, material_category: v })}>
                  <SelectTrigger data-testid="material-form-category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__new__">+ Add new category</SelectItem>
                  </SelectContent>
                </Select>
                {dialog.form._newCat && (
                  <Input value={dialog.form.material_category} autoComplete="off" className="mt-2" onChange={e => setForm({ material_category: e.target.value })} placeholder="New category name" data-testid="material-form-category" />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Item Name</Label>
                <Input value={dialog.form.item_name} onChange={e => setForm({ item_name: e.target.value })} placeholder="e.g. Portland Cement" data-testid="material-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Unit Type</Label>
                  <Input value={dialog.form.unit_type} onChange={e => setForm({ unit_type: e.target.value })} placeholder="e.g. bag" data-testid="material-form-unit" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Unit Cost</Label>
                  <Input type="number" value={dialog.form.unit_cost} onChange={e => setForm({ unit_cost: e.target.value })} data-testid="material-form-cost" />
                </div>
              </div>
            </form>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="material-form-cancel">Cancel</Button>
            <Button onClick={save} data-testid="material-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyMat} onOpenChange={(o) => !o && setHistoryMat(null)}>
        <DialogContent data-testid="material-history-dialog">
          <DialogHeader><DialogTitle>Price History — {historyMat?.item_name}</DialogTitle></DialogHeader>
          {historyMat && (() => {
            const hist = (historyMat.price_history && historyMat.price_history.length
              ? historyMat.price_history
              : [{ unit_cost: historyMat.unit_cost, changed_at: historyMat.updated_at || historyMat.created_at }]).slice().reverse();
            return (
              <div className="max-h-72 overflow-y-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-right">Unit Cost</th><th className="p-2 text-right w-28">Change</th></tr></thead>
                  <tbody>
                    {hist.map((h, idx) => {
                      const prev = hist[idx + 1];
                      const delta = prev ? Number(h.unit_cost) - Number(prev.unit_cost) : 0;
                      return (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{fmtDate(h.changed_at)}</td>
                          <td className="p-2 text-right">{peso(h.unit_cost)}</td>
                          <td className="p-2 text-right">
                            {delta === 0 ? <span className="text-muted-foreground">—</span>
                              : delta > 0 ? <span className="text-red-600 inline-flex items-center gap-0.5"><ArrowUp className="w-3 h-3" />{peso(Math.abs(delta))}</span>
                              : <span className="text-emerald-600 inline-flex items-center gap-0.5"><ArrowDown className="w-3 h-3" />{peso(Math.abs(delta))}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryMat(null)} data-testid="material-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
